import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteAllSearchHistory,
  deleteSearchHistory,
  getSearchHistory,
  pageInitialState,
} from 'store';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'lib';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persianFa from 'react-date-object/locales/persian_fa';
import { UiButton, UiIcon, UiTooltip } from 'ui-components';
import { StyledMeuContainer } from './searchHistoryMenu.styles';
import SearchHistorySkeleton from './SearchHistorySkeleton';

const DELETE_DELAY = 3000;

function SearchHistoryMenu({ setSearchText, setIsInputFocused, searchType }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deleteAllTimeoutRef = useRef(null);

  const [isDeletingAllHistory, setIsDeletingAllHistory] = useState(false);
  const [tooltipQuery, setTooltipQuery] = useState(null);

  const { loading: searchHistoryLoading, data } = useSelector(
    (state) => state.pageReducer.searchHistory ?? pageInitialState.searchHistory
  );

  const { loading: deleteSearchHistoryLoading } = useSelector(
    (state) =>
      state.pageReducer.deleteSearchHistory ??
      pageInitialState.deleteSearchHistory
  );

  const { loading: deleteAllSearchHistoryLoading } = useSelector(
    (state) =>
      state.pageReducer.deleteAllSearchHistory ??
      pageInitialState.deleteAllSearchHistory
  );

  useEffect(() => {
    dispatch(
      getSearchHistory({
        searchType: searchType === 'resource' ? 'resources' : 'contents',
      })
    );
    return () => {
      if (deleteAllTimeoutRef.current) {
        clearTimeout(deleteAllTimeoutRef.current);
      }
    };
  }, [searchType, dispatch]);

  const keepInputFocused = (event) => {
    event.preventDefault();
  };

  const handleClickHistoryItem = (query) => {
    setSearchText(query);
    setIsInputFocused(true);
  };

  const handleContentMouseEnter = (event, query) => {
    const element = event.currentTarget;
    const isTruncated = element.scrollWidth > element.clientWidth;

    setTooltipQuery(isTruncated ? query : null);
  };

  const handleDeleteHistoryItem = async (event, query) => {
    event.stopPropagation();

    await dispatch(deleteSearchHistory({ query })).unwrap();

    dispatch(
      getSearchHistory({
        searchType: searchType === 'resource' ? 'resources' : 'contents',
      })
    );
  };

  const sendDeleteAllRequest = async () => {
    try {
      await dispatch(deleteAllSearchHistory()).unwrap();

      dispatch(
        getSearchHistory({
          searchType: searchType === 'resource' ? 'resources' : 'contents',
        })
      );
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t('search:error.deleteAllSearchHistory.description'),
      });
    } finally {
      setIsDeletingAllHistory(false);
      deleteAllTimeoutRef.current = null;
    }
  };

  const handleDeleteAllHistory = () => {
    setIsDeletingAllHistory(true);

    deleteAllTimeoutRef.current = setTimeout(() => {
      sendDeleteAllRequest();
    }, DELETE_DELAY);
  };

  const handleUndoDeleteAllHistory = () => {
    if (deleteAllTimeoutRef.current) {
      clearTimeout(deleteAllTimeoutRef.current);
      deleteAllTimeoutRef.current = null;
    }

    setIsDeletingAllHistory(false);
  };

  const formatDate = (date) =>
    new DateObject(new Date(date))
      .convert(persian, persianFa)
      .format('YYYY/MM/DD HH:mm');

  if (!searchHistoryLoading && data.length === 0) {
    return null;
  }

  return (
    <div className='search-history-dropdown' onMouseDown={keepInputFocused}>
      <StyledMeuContainer isDeletingAllHistory={isDeletingAllHistory}>
        <div className='history-container'>
          <h2
            className='history-header'
            style={{
              textAlign: isDeletingAllHistory ? 'center' : 'right',
              margin: isDeletingAllHistory ? '24px' : '0',
            }}
          >
            {isDeletingAllHistory
              ? t('search:history.deleted.title')
              : t('search:history.title')}
          </h2>

          <div
            className={`delete-progress ${
              isDeletingAllHistory ? 'delete-progress--active' : ''
            }`}
          >
            <span />
          </div>

          {!isDeletingAllHistory && (
            <div className='history-items-wrapper'>
              {searchHistoryLoading || deleteSearchHistoryLoading ? (
                <SearchHistorySkeleton count={data.length} />
              ) : (
                data.map((d) => (
                  <div
                    key={d.q}
                    className='history-item'
                    onClick={() => handleClickHistoryItem(d.q)}
                  >
                    <span className='date' dir='ltr'>
                      {formatDate(d.created_at)}
                    </span>

                    <UiTooltip
                      style={{ width: '100%', minWidth: 0 }}
                      title={tooltipQuery === d.q ? d.q : undefined}
                    >
                      <p
                        className='content'
                        onMouseEnter={(event) =>
                          handleContentMouseEnter(event, d.q)
                        }
                        onMouseLeave={() => setTooltipQuery(null)}
                      >
                        {d.q}
                      </p>
                    </UiTooltip>

                    <UiIcon
                      className='clear-icon'
                      icon='ri-close-line'
                      size={20}
                      onClick={(event) => handleDeleteHistoryItem(event, d.q)}
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {!isDeletingAllHistory && <div className='divider' />}

          <UiButton
            className='clear-all'
            variant='text'
            color={isDeletingAllHistory ? 'primary' : 'error'}
            size='small'
            style={{
              width: 'fit-content',
              alignSelf: 'flex-end',
              marginTop: '8px',
            }}
            onClick={
              isDeletingAllHistory
                ? handleUndoDeleteAllHistory
                : handleDeleteAllHistory
            }
            disabled={deleteAllSearchHistoryLoading}
            loading={deleteAllSearchHistoryLoading}
          >
            <div className='flex gap-2'>
              {isDeletingAllHistory && (
                <UiIcon icon='ri-arrow-go-back-line' size={18} />
              )}

              {isDeletingAllHistory
                ? t('search:history.deleted.button')
                : t('search:history.deleteAll.button')}
            </div>
          </UiButton>
        </div>
      </StyledMeuContainer>
    </div>
  );
}

SearchHistoryMenu.propTypes = {
  setSearchText: PropTypes.func.isRequired,
  setIsInputFocused: PropTypes.func.isRequired,
  searchType: PropTypes.string.isRequired,
};

export default SearchHistoryMenu;
